import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';
import { useTheme } from '@/contexts/ThemeContext';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground shadow hover:bg-primary/90',
        destructive: 'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
        outline:
          'border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-md px-8',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    const { theme } = useTheme();

    // Create theme-aware button styles
    const getVariantStyles = () => {
      if (!variant || variant === 'default') {
        return theme === 'dark'
          ? 'bg-primary text-primary-foreground shadow hover:bg-primary/90 focus-visible:ring-primary'
          : 'bg-primary text-primary-foreground shadow hover:bg-primary/90 focus-visible:ring-primary';
      }

      if (variant === 'destructive') {
        return theme === 'dark'
          ? 'bg-red-600 text-white shadow-sm hover:bg-red-700 focus-visible:ring-red-500'
          : 'bg-red-600 text-white shadow-sm hover:bg-red-700 focus-visible:ring-red-500';
      }

      if (variant === 'outline') {
        return theme === 'dark'
          ? 'border border-slate-600 bg-transparent text-slate-100 shadow-sm hover:bg-slate-800 hover:text-slate-50 focus-visible:ring-slate-500'
          : 'border border-slate-300 bg-white text-slate-900 shadow-sm hover:bg-slate-100 hover:text-slate-950 focus-visible:ring-slate-500';
      }

      if (variant === 'secondary') {
        return theme === 'dark'
          ? 'bg-slate-700 text-slate-100 shadow-sm hover:bg-slate-600 focus-visible:ring-slate-500'
          : 'bg-slate-200 text-slate-900 shadow-sm hover:bg-slate-300 focus-visible:ring-slate-500';
      }

      if (variant === 'ghost') {
        return theme === 'dark'
          ? 'text-slate-100 hover:bg-slate-800 hover:text-slate-50 focus-visible:ring-slate-500'
          : 'text-slate-900 hover:bg-slate-100 hover:text-slate-950 focus-visible:ring-slate-500';
      }

      if (variant === 'link') {
        return theme === 'dark'
          ? 'text-blue-400 underline-offset-4 hover:underline focus-visible:ring-blue-400'
          : 'text-blue-600 underline-offset-4 hover:underline focus-visible:ring-blue-600';
      }

      return '';
    };

    return (
      <Comp
        className={cn(
          'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
          getVariantStyles(),
          size === 'sm' && 'h-8 rounded-md px-3 text-xs',
          size === 'lg' && 'h-10 rounded-md px-8',
          size === 'icon' && 'h-9 w-9',
          (!size || size === 'default') && 'h-9 px-4 py-2',
          className
        )}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
